import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Mail, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Gmail Settings Component
 * Shows email configuration status.
 */
const GmailSettings = ({ getAuthHeaders }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/gmail/status`, {
        headers: getAuthHeaders(),
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch email status:', error);
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Integration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email Integration
        </CardTitle>
        <CardDescription>
          Send email notifications for deal events (new deals, funding, approvals, declines).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Status */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              {status?.connected ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : (
                <XCircle className="h-6 w-6 text-gray-400" />
              )}
              <div>
                <p className="font-medium">
                  {status?.connected ? 'Connected' : 'Not Connected'}
                </p>
                {status?.email && (
                  <p className="text-sm text-gray-600">{status.email}</p>
                )}
              </div>
            </div>
            <Badge variant={status?.connected ? 'default' : 'secondary'}>
              {status?.connected ? 'Active' : 'Inactive'}
            </Badge>
          </div>

          {/* Help Text */}
          <div className="text-sm text-gray-500 bg-blue-50 p-3 rounded-lg">
            <p className="font-medium text-blue-700 mb-1">How to send emails:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-600">
              <li>Go to any Deal detail page</li>
              <li>Click the "Send Email" button in the header</li>
              <li>Choose the email type (New Deal, Funding, Approval, Decline)</li>
              <li>Review and confirm before sending</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default GmailSettings;
